// "Create prequalification site" — the create-in-context affordance on the
// Broker and Brokerage detail pages. Mirrors the NewDealWizard precedent
// (initialBrokerId / initialBrokerName): the record you launched from is
// pre-associated with the new partner brand, and stays changeable inside the
// wizard.
//
// 🔴 IT REPORTS INTENT AND MOUNTS NOTHING. It used to hold the popup's `open`
// state and mount PartnerBrandWizardModal itself, which put the wizard's entire
// lifetime inside whichever branch of the host's `site ? … : …` conditional the
// button happened to sit in. Step 1 of the wizard persists a real brand row and
// invalidates the site read, so that branch flips WHILE the wizard is open: the
// launcher unmounted and took the wizard with it, mid-flow, on the success path,
// after the row had already been written. Modal portals to <body>, but a portal
// does not outlive the component that owns it. The launch state now lives above
// the conditional in PrequalificationSiteTab and the wizard is mounted there as
// its sibling — see CreateSiteWizardMount. This component stays a button.
//
// The wizard opens as a POPUP ON THE RECORD. It used to navigate to
// /partners/brands/new and land on the brand detail page afterwards, so the
// staff member never got back to where they started; now nothing navigates and
// finishing refreshes the record's site read in place. The route still exists
// for the Partner Brands list, and renders the same wizard body.
//
// Self-gates on Partners.ManageBrands (AdminUndoButton precedent — no permission
// plumbing in the host page). That gate is also what keeps the hoisted wizard
// unreachable without the permission: nothing else raises the launch.

import { Globe } from "lucide-react";
import { Button } from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";

/** Which record a new site should be owned by — the shape that TRAVELS. The host
 *  holds one of these as its launch state and hands it to the wizard, and the
 *  route below serialises the same fields into a query string. One definition, so
 *  the popup and the route cannot describe an owner differently. */
export interface PrequalSiteLaunchContext {
  /** Launched from a broker record — pre-sets the owning broker. */
  ownerBrokerId?: string;
  ownerBrokerName?: string | null;
  /** Launched from a brokerage record — pre-sets the owning brokerage. */
  ownerBrokerageId?: string;
  ownerBrokerageName?: string | null;
  // There is deliberately NO buyer-team arm (D2a): a team's site is minted with
  // the team by BuyerTeamService.CreateAsync, Phoenix-branded, and the server
  // refuses a staff create that names a team as owner.
}

/** Query string the /partners/brands/new ROUTE reads for its pre-set owner.
 *  The in-record launch no longer uses it — the popup passes the owner as props
 *  — but the route still honours it for a hand-built or bookmarked link. */
export function prequalSiteLaunchQuery(context: PrequalSiteLaunchContext): string {
  const params = new URLSearchParams();
  if (context.ownerBrokerId) {
    params.set("ownerBrokerId", context.ownerBrokerId);
    if (context.ownerBrokerName) params.set("ownerBrokerName", context.ownerBrokerName);
  } else if (context.ownerBrokerageId) {
    params.set("ownerBrokerageId", context.ownerBrokerageId);
    if (context.ownerBrokerageName)
      params.set("ownerBrokerageName", context.ownerBrokerageName);
  }
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

interface Props {
  /** Raise the launch. The HOST owns which record the site is for and mounts the
   *  wizard; this button only reports that someone asked for one. */
  onLaunch: () => void;
}

export function CreatePrequalSiteButton({ onLaunch }: Props) {
  const { hasPermission } = useAuth();
  if (!hasPermission("Partners.ManageBrands")) return null;

  return (
    <Button size="sm" variant="secondary" onClick={onLaunch}>
      <Globe size={14} strokeWidth={1.5} aria-hidden /> Create prequalification site
    </Button>
  );
}
